import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Alert, Space, Button, Popconfirm, message,
  Modal, Form, Input, Divider, Row, Col, Card, Upload, Tag, Empty, Spin, Select
} from 'antd';
import { UploadOutlined, PlusOutlined, SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServices, deleteService, updateService, setHomepageOrder, createService } from '../../store/features/dashboard/serviceSlice';
import { S3_CONFIG } from '../../config/s3';
import { debounce, beforeUploadValidation } from '../../lib/utils';
const { TextArea } = Input;
const { Option } = Select;

const Services = () => {
  const dispatch = useDispatch();
  const {
    services = [],
    loading,
    error,
    total = 0,
    currentPage = 1,
    pageSize = 10,
  } = useSelector((state) => state.services);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentServiceId, setCurrentServiceId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [deletedFilter, setDeletedFilter] = useState('');
  const [homepageFilter, setHomepageFilter] = useState('');
  const [form] = Form.useForm();
  const searchInputRef = useRef(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleOrderClick = (service) => {
    setSelectedService({
      ...service,
      homepageOrder: service.homepage_order || 0
    });
    setOrderModalVisible(true);
  };

  const handleOrderUpdate = async () => {
    try {
      const existingService = services.find(s =>
        s.homepage_order === selectedService.homepageOrder &&
        s.id !== selectedService.id
      );

      if (existingService && selectedService.homepageOrder > 0) {
        Modal.confirm({
          title: 'Position Already Taken',
          content: `Position ${selectedService.homepageOrder} is currently held by "${existingService.title}". Do you want to replace it?`,
          onOk: async () => {
            try {
              await dispatch(setHomepageOrder({
                serviceId: selectedService.id,
                homepageOrder: selectedService.homepageOrder
              })).unwrap();

              message.success('Homepage positions updated!');
              fetchData(currentPage);
              setOrderModalVisible(false);
            } catch (err) {
              message.error(err.message || 'Failed to update positions');
            }
          }
        });
      } else {
        await dispatch(setHomepageOrder({
          serviceId: selectedService.id,
          homepageOrder: selectedService.homepageOrder
        })).unwrap();

        message.success(
          selectedService.homepageOrder > 0
            ? `Service set to position ${selectedService.homepageOrder}`
            : 'Service removed from homepage'
        );
        fetchData(currentPage);
        setOrderModalVisible(false);
      }
    } catch (err) {
      message.error(err.message || 'Failed to update position');
    }
  };

  const fetchData = useCallback((page, size, search = '', deleted = deletedFilter, homepage = homepageFilter) => {
    setIsSearching(true);
    dispatch(fetchServices({
      page,
      limit: size || pageSize,
      search,
      deleted: deleted === 'true' ? 'true' : '',
      homepage: homepage === 'true' ? 'true' : ''
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch, pageSize, deletedFilter, homepageFilter]);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchData(1, pageSize, searchValue);
      }
    }, 500),
    [fetchData, pageSize]
  );

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value);
    } else {
      fetchData(1, pageSize, value);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchData(1, pageSize, '');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, deletedFilter, homepageFilter]);

  const handleDelete = async (serviceId) => {
    try {
      await dispatch(deleteService(serviceId)).unwrap();
      message.success('Service soft deleted successfully!');
      if (services.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1);
      } else {
        fetchData(currentPage);
      }
    } catch (error) {
      message.error(error.message || 'Failed to soft delete service');
    }
  };

  const handleEdit = (service) => {
    if (service.status === 'deleted') {
      message.error('Cannot edit a deleted service');
      return;
    }
    setIsEditMode(true);
    setCurrentServiceId(service.id);
    setIsModalVisible(true);

    const imageFields = {};
    if (Array.isArray(service.images)) {
      service.images.forEach((image, index) => {
        if (image && typeof image === 'object' && image.url && Number.isInteger(image.order)) {
          const imageKey = [
            'iconImage',
            'thumbnailImage',
            'featuredImage',
            'galleryImage1',
            'galleryImage2',
          ][image.order];
          if (imageKey) {
            imageFields[imageKey] = [{
              uid: `-${index}`,
              name: image.url.split('/').pop(),
              status: 'done',
              url: `${S3_CONFIG.publicUrl}/${image.url}`,
            }];
          }
        }
      });
    }

    const imageFieldNames = ['iconImage', 'thumbnailImage', 'featuredImage', 'galleryImage1', 'galleryImage2'];
    imageFieldNames.forEach(field => {
      if (!imageFields[field]) {
        imageFields[field] = [];
      }
    });

    form.setFieldsValue({
      title: service.title || '',
      category: service.category || '',
      description: service.description || '',
      benefit_description: service.benefit_description || '',
      faq_description: service.faq_description || '',
      details: service.details || '',
      ...imageFields,
      whyImportant: service.whyImportant || { title: '', description: '' },
      benefits: (() => {
        const b = service.benefits || [];
        return [b[0] || '', b[1] || '', b[2] || ''];
      })(),
      faqs: (() => {
        const f = service.faqs || [];
        return [
          f[0] || { question: '', answer: '' },
          f[1] || { question: '', answer: '' },
          f[2] || { question: '', answer: '' },
          f[3] || { question: '', answer: '' },
        ];
      })(),
    });
  };

  const validateFields = (values) => {
    const requiredFields = [
      { field: 'title', message: 'Please add title to the service' },
      { field: 'category', message: 'Please add category to the service' },
      { field: 'description', message: 'Please add description to the service' },
      { field: 'details', message: 'Please add details to the service' },
    ];

    for (const { field, message } of requiredFields) {
      if (!values[field]?.trim()) {
        message.error(message);
        return false;
      }
    }

    if (!values.benefits || values.benefits.length !== 3 || values.benefits.some(b => !b?.trim())) {
      message.error('Please fill all 3 benefit fields');
      return false;
    }

    if (!values.faqs || values.faqs.length !== 4 || values.faqs.some(f => !f?.question?.trim() || !f?.answer?.trim())) {
      message.error('Please fill all 4 FAQ questions and answers');
      return false;
    }

    if (!values.whyImportant || !values.whyImportant.title?.trim() || !values.whyImportant.description?.trim()) {
      message.error('Please fill both why important title and description');
      return false;
    }

    const imageFields = ['iconImage', 'thumbnailImage', 'featuredImage', 'galleryImage1', 'galleryImage2'];
    const missingImages = imageFields.filter(field => !values[field] || values[field].length === 0);
    if (missingImages.length > 0) {
      message.error(`Please upload all 5 images (missing: ${missingImages.join(', ')})`);
      return false;
    }

    return true;
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (!validateFields(values)) {
        return;
      }

      const formData = new FormData();

      formData.append('title', values.title.trim());
      formData.append('category', values.category.trim());
      formData.append('description', values.description.trim());
      formData.append('details', values.details.trim());
      formData.append('benefit_description', values.benefit_description?.trim() || '');
      formData.append('faq_description', values.faq_description?.trim() || '');

      formData.append('whyImportant[title]', values.whyImportant.title.trim());
      formData.append('whyImportant[description]', values.whyImportant.description.trim());

      formData.append('benefits', JSON.stringify(values.benefits.filter(b => b.trim())));

      formData.append('faqs', JSON.stringify(
        values.faqs.filter(f => f.question.trim() && f.answer.trim())
      ));

      const imageFields = [
        'iconImage',
        'thumbnailImage',
        'featuredImage',
        'galleryImage1',
        'galleryImage2',
      ];
      const newFiles = [];
      const fileOrders = [];
      const existingImages = [];

      imageFields.forEach((fieldName, index) => {
        const fileList = values[fieldName];
        if (fileList && fileList[0]) {
          const fileInfo = fileList[0];
          const order = index;

          if (fileInfo.originFileObj) {
            newFiles.push(fileInfo.originFileObj);
            fileOrders.push(order);
          } else if (fileInfo.url) {
            const cleanUrl = fileInfo.url.replace(`${S3_CONFIG.publicUrl}/`, '');
            existingImages.push({ url: cleanUrl, order });
          }
        }
      });

      if (newFiles.length + existingImages.length !== 5) {
        message.error('Exactly 5 images are required (new or existing)');
        return;
      }
      if (fileOrders.length > 0) {
        const uniqueOrders = new Set(fileOrders);
        if (uniqueOrders.size !== fileOrders.length || fileOrders.some(order => order < 0 || order > 4)) {
          message.error('Invalid file orders: must be unique integers between 0 and 4');
          return;
        }
      }
      if (existingImages.length > 0) {
        const uniqueOrders = new Set(existingImages.map(img => img.order));
        if (uniqueOrders.size !== existingImages.length || existingImages.some(img => img.order < 0 || img.order > 4)) {
          message.error('Invalid existing image positions: must be unique integers between 0 and 4');
          return;
        }
      }

      newFiles.forEach(file => {
        formData.append('images', file);
      });
      if (fileOrders.length > 0) {
        formData.append('fileOrders', JSON.stringify(fileOrders));
      }
      if (existingImages.length > 0) {
        formData.append('existingImagePositions', JSON.stringify(existingImages));
      }

      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      if (isEditMode) {
        await dispatch(
          updateService({
            serviceId: currentServiceId,
            formData,
          })
        ).unwrap();
        message.success('Service updated successfully');
      } else {
        await dispatch(createService(formData)).unwrap();
        message.success('Service created successfully');
      }
      fetchData(currentPage);
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit service';
      if (typeof errorMessage === 'string') {
        message.error(errorMessage);
      } else {
        message.error('An unexpected error occurred');
      }
    }
  };

  const resetModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setCurrentServiceId(null);
    setExistingImages([]);
    form.resetFields();
  };

  const handleCancel = () => {
    resetModal();
  };

  const handlePageChange = (page, size) => {
    fetchData(page, size);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList;
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: category => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Homepage Position',
      dataIndex: 'homepage_order',
      key: 'homepage_order',
      width: 180,
      render: (order, record) => {
        const isConflict = services.filter(s =>
          s.homepage_order === order && s.id !== record.id && order > 0
        ).length > 0;

        return (
          <Tag
            color={order > 0 ? (isConflict ? 'red' : 'green') : 'orange'}
            style={{ cursor: record.status === 'deleted' ? 'not-allowed' : 'pointer' }}
            onClick={() => record.status !== 'deleted' && handleOrderClick(record)}
          >
            {order > 0 ? `Position ${order}${isConflict ? ' (Conflict!)' : ''}` : 'Not on homepage'}
          </Tag>
        );
      }
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            onClick={() => handleEdit(record)} 
            disabled={record.status === 'deleted'}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to soft delete this service?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.status === 'deleted'}
          >
            <Button 
              danger 
              disabled={record.status === 'deleted'}
              loading={loading}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && services.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Services"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Services Management</h2>

      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsEditMode(false);
                setIsModalVisible(true);
              }}
            >
              Add Service
            </Button>
          </Col>
          <Col>
            <Space>
              <Select
                value={deletedFilter}
                allowClear
                onChange={(value) => {
                  setDeletedFilter(value || '');
                  fetchData(1, pageSize, searchText, value || '', homepageFilter);
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Deleted Only</Option>
              </Select>
              <Select
                value={homepageFilter}
                allowClear
                onChange={(value) => {
                  setHomepageFilter(value || '');
                  fetchData(1, pageSize, searchText, deletedFilter, value || '');
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Homepage Only</Option>
              </Select>
              <Input.Search
                ref={searchInputRef}
                placeholder="Search services..."
                allowClear
                enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
                size="large"
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value, e)}
                onSearch={(value) => handleSearch(value, { type: 'search' })}
                onClear={handleClearSearch}
                loading={isSearching}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px' }}>
        {services.length === 0 && !loading ? (
          <Empty
            description={
              searchText || deletedFilter || homepageFilter
                ? 'No services found with the applied filters'
                : 'No services available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={services}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>

      <Modal
        title={isEditMode ? 'Edit Service' : 'Add New Service'}
        width={800}
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleOk}
        okText={isEditMode ? 'Update' : 'Create'}
        cancelText="Cancel"
        confirmLoading={loading}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter service title' }]}
              >
                <Input placeholder="Service title" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please enter service category' }]}
              >
                <Input placeholder="Service category" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description (4 lines)"
                rules={[{ required: true, message: 'Please enter service description' }]}
              >
                <TextArea rows={4} placeholder="Service description" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="details"
                label="Details (5 lines)"
                rules={[{ required: true, message: 'Please enter service details' }]}
              >
                <TextArea rows={5} placeholder="Detailed information about the service" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Media</Divider>

          <Form.Item
            name="iconImage"
            label="1. Icon Image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: 'Please upload icon image' }]}
          >
            <Upload
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess({ url: URL.createObjectURL(file) });
                }, 1000);
              }}
              listType="picture-card"
              maxCount={1}
              beforeUpload={(file) => beforeUploadValidation(file, 'Icon image', 5, 128, 128)}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div>Upload Icon</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="thumbnailImage"
            label="2. Thumbnail Image (372x288px) - Used in listings"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: 'Please upload thumbnail image' }]}
          >
            <Upload
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess({ url: URL.createObjectURL(file) });
                }, 1000);
              }}
              listType="picture-card"
              maxCount={1}
              beforeUpload={(file) => beforeUploadValidation(file, 'Thumbnail image', 5, 372, 288)}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div>Upload Thumbnail</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="featuredImage"
            label="3. Featured Image (768x384px) - Main display image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: 'Please upload featured image' }]}
          >
            <Upload
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess({ url: URL.createObjectURL(file) });
                }, 1000);
              }}
              listType="picture-card"
              maxCount={1}
              beforeUpload={(file) => beforeUploadValidation(file, 'Featured image', 5, 768, 384)}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div>Upload Featured</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="galleryImage1"
            label="4. Gallery Image 1 (376x232px) - Detail page image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: 'Please upload gallery image 1' }]}
          >
            <Upload
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess({ url: URL.createObjectURL(file) });
                }, 1000);
              }}
              listType="picture-card"
              maxCount={1}
              beforeUpload={(file) => beforeUploadValidation(file, 'Gallery image 1', 5, 376, 232)}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div>Upload Gallery 1</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="galleryImage2"
            label="5. Gallery Image 2 (376x232px) - Detail page image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: 'Please upload gallery image 2' }]}
          >
            <Upload
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess({ url: URL.createObjectURL(file) });
                }, 1000);
              }}
              listType="picture-card"
              maxCount={1}
              beforeUpload={(file) => beforeUploadValidation(file, 'Gallery image 2', 5, 376, 232)}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div>Upload Gallery 2</div>
              </div>
            </Upload>
          </Form.Item>

          <Card title="Why Important" size="small">
            <Form.Item
              name={['whyImportant', 'title']}
              label="Title"
              rules={[{ required: true, message: 'Please enter title' }]}
            >
              <Input placeholder="Why this service is important" />
            </Form.Item>
            <Form.Item
              name={['whyImportant', 'description']}
              label="Description (2 lines)"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <Input.TextArea rows={2} placeholder="Detailed explanation" />
            </Form.Item>
          </Card>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="benefit_description"
                label="Benefits Description (4 lines)"
                rules={[{ required: true, message: 'Please enter benefits description' }]}
              >
                <TextArea rows={4} placeholder="Benefits description" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Benefits (exactly 3 required)</Divider>

          <Form.Item
            name={['benefits', 0]}
            label="Benefit 1"
            rules={[{ required: true, message: 'Please enter benefit 1' }]}
          >
            <Input placeholder="Enter benefit 1" />
          </Form.Item>

          <Form.Item
            name={['benefits', 1]}
            label="Benefit 2"
            rules={[{ required: true, message: 'Please enter benefit 2' }]}
          >
            <Input placeholder="Enter benefit 2" />
          </Form.Item>

          <Form.Item
            name={['benefits', 2]}
            label="Benefit 3"
            rules={[{ required: true, message: 'Please enter benefit 3' }]}
          >
            <Input placeholder="Enter benefit 3" />
          </Form.Item>

          <Divider orientation="left">FAQs (exactly 4 required)</Divider>

          <Form.Item
            name={['faqs', 0, 'question']}
            label="Question 1"
            rules={[{ required: true, message: 'Please enter question 1' }]}
          >
            <Input placeholder="Enter question 1" />
          </Form.Item>
          <Form.Item
            name={['faqs', 0, 'answer']}
            label="Answer 1"
            rules={[{ required: true, message: 'Please enter answer 1' }]}
          >
            <TextArea rows={2} placeholder="Enter answer 1" />
          </Form.Item>
          <Divider dashed />

          <Form.Item
            name={['faqs', 1, 'question']}
            label="Question 2"
            rules={[{ required: true, message: 'Please enter question 2' }]}
          >
            <Input placeholder="Enter question 2" />
          </Form.Item>
          <Form.Item
            name={['faqs', 1, 'answer']}
            label="Answer 2"
            rules={[{ required: true, message: 'Please enter answer 2' }]}
          >
            <TextArea rows={2} placeholder="Enter answer 2" />
          </Form.Item>
          <Divider dashed />

          <Form.Item
            name={['faqs', 2, 'question']}
            label="Question 3"
            rules={[{ required: true, message: 'Please enter question 3' }]}
          >
            <Input placeholder="Enter question 3" />
          </Form.Item>
          <Form.Item
            name={['faqs', 2, 'answer']}
            label="Answer 3"
            rules={[{ required: true, message: 'Please enter answer 3' }]}
          >
            <TextArea rows={2} placeholder="Enter answer 3" />
          </Form.Item>
          <Divider dashed />

          <Form.Item
            name={['faqs', 3, 'question']}
            label="Question 4"
            rules={[{ required: true, message: 'Please enter question 4' }]}
          >
            <Input placeholder="Enter question 4" />
          </Form.Item>
          <Form.Item
            name={['faqs', 3, 'answer']}
            label="Answer 4"
            rules={[{ required: true, message: 'Please enter answer 4' }]}
          >
            <TextArea rows={2} placeholder="Enter answer 4" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Set Homepage Position"
        visible={orderModalVisible}
        onOk={handleOrderUpdate}
        onCancel={() => setOrderModalVisible(false)}
      >
        {selectedService && (
          <div>
            <p>Service: <strong>{selectedService.title}</strong></p>
            <Select
              style={{ width: '100%' }}
              value={selectedService.homepageOrder || 0}
              onChange={(value) => setSelectedService({
                ...selectedService,
                homepageOrder: value
              })}
            >
              <Select.Option value={0}>Not on homepage</Select.Option>
              <Select.Option value={1}>Position 1 (Featured)</Select.Option>
              <Select.Option value={2}>Position 2</Select.Option>
              <Select.Option value={3}>Position 3</Select.Option>
              <Select.Option value={4}>Position 4</Select.Option>
            </Select>
            <p style={{ marginTop: 16, color: '#666' }}>
              Only 4 services can be featured on homepage. Selecting a position will replace any existing service in that spot.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Services;